import React, { useCallback, useEffect, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import useApi from "modules/rewards"
import { useAuthContext } from "contexts/AuthContext"
import StickerItem from "../grades/components/StickerItem"
import moment from "moment"
import Diploma from "./diplomas/Diploma"
import { Box, Button, Stack } from "@mui/material"
import JsPDF from "jspdf"
import html2canvas from "html2canvas"
import { useTranslation } from "react-i18next"
import Swal from "sweetalert2"
import withReactContent from "sweetalert2-react-content"
import LoaderModal from "components/UI/LoaderModal"
import { Splide, SplideSlide, SplideTrack } from "@splidejs/react-splide"
import "@splidejs/react-splide/css"
import CloseButton from "components/UI/CloseButton"
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight"
import {
  openBadge,
  openSticker,
  openDiploma,
  nextNotification,
} from "utils/sounds"
import useSound from "hooks/useSound"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import { styles } from "./styles/RewardsItem.styles"
import { GROUP_TYPES } from "common/constants"
import Slider from "react-slick"
import "slick-carousel/slick/slick.css"
import "slick-carousel/slick/slick-theme.css"
import { isRunningFromIframe } from "common/functions"

const settings = {
  className: "",
  dots: true,
  arrows: false,
  infinite: true,
  slidesToShow: 1,
  slidesToScroll: 1,
  adaptiveHeight: false,
}

const v2 = false

export default function RewardsItem({ params }) {
  const {
    getReward,
    getRewardByAssignations,
    getCurriculumReward,
    getUnreadRewards,
    setRewardRead,
  } = useApi()
  const [queryParams] = useSearchParams()
  const { selectedGroupData, userData, audioSettings } = useAuthContext()
  const diplomaContainersRef = useRef([])
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const MySwal = withReactContent(Swal)
  const [items, setItems] = useState([])
  const splideRef = useRef(null)
  const audiosRef = useRef([])
  const [activeItemIndex, setActiveItemIndex] = useState(0)
  const readStatusRef = useRef([])
  const [playBadge] = useSound(openBadge, {
    volume: 0.5,
    soundEnabled: audioSettings?.enabledAudioEffects,
    interrupt: true,
  })
  const [playSticker] = useSound(openSticker, {
    volume: 0.5,
    soundEnabled: audioSettings?.enabledAudioEffects,
    interrupt: true,
  })
  const [playDiploma] = useSound(openDiploma, {
    volume: 0.5,
    soundEnabled: audioSettings?.enabledAudioEffects,
    interrupt: true,
  })
  const [playNextNotification] = useSound(nextNotification, {
    volume: 0.5,
    soundEnabled: audioSettings?.enabledAudioEffects,
    interrupt: true,
  })
  const selectedIndexRef = useRef()

  useEffect(() => {
    // set backgroundColor for body
    document.body.style.backgroundColor = "transparent"

    window.donwloadDiploma = generatePdf
    return () => {
      window.donwloadDiploma = undefined
    }
  }, [])

  useEffect(() => {
    if (items.length === 0 || selectedIndexRef.current === activeItemIndex)
      return
    console.log("activeItemIndex", activeItemIndex)
    selectedIndexRef.current = activeItemIndex
    //   console.log(items[activeItemIndex]?.categoryType,"sounds[items[activeItemIndex]?.categoryType")
    // play audio
    const sounds = {
      1: playBadge,
      2: playSticker,
      3: playDiploma,
    }
    const sound = sounds[items[activeItemIndex]?.categoryType]
    if (sound) {
      sound()
      setTimeout(() => {
        if (audiosRef?.current[activeItemIndex]) {
          audiosRef?.current[activeItemIndex]?.play()
        }
      }, 1500)
    } else {
      if (audiosRef?.current[activeItemIndex]) {
        audiosRef?.current[activeItemIndex]?.play()
      }
    }
  }, [activeItemIndex, items])

  useEffect(() => {
    if (
      (queryParams.size === 0 && Object.keys(params).length === 0) ||
      userData?.userId < 0 ||
      selectedGroupData?.groupId < 0
    )
      return
    const itemId = queryParams.get("itemId") || params?.itemId || 0
    const unreads = queryParams.get("unreads") || params?.unreads || "0"
    const userItemId = queryParams.get("userItemId") || params?.userItemId || 0
    const items = params?.items || []
    const runningFromIframe = isRunningFromIframe()
    if (unreads === "1") {
      if (items.length > 0) {
        getFromParams(items)
        return
      }
      const languageId = userData?.isB2C
        ? userData?.b2C?.languageToLearn
        : selectedGroupData?.learningLanguageId
      const groupType =
        selectedGroupData?.groupTypeId > 0
          ? selectedGroupData?.groupTypeId
          : GROUP_TYPES.SECONDARY_REGULAR_CLASS
      getUnreadRewards({ languageId })
        .then(async (results) => {
          const data = []
          try {
            for (const response of results) {
              const userRecognitionItemId =
                response.assignations[0]?.userRecognitionItemId || 0
              if (response?.recognitionSectionTypeId === 3) {
                const groupId = selectedGroupData?.groupId
                const item = response
                const json = JSON.parse(item.description)
                const assignation = item.assignations.find(
                  (a) => a.userId === userData?.userId
                )
                if (assignation) {
                  const date = moment(assignation?.visibleFrom).format(
                    "MMMM Do, YYYY"
                  )
                  const settings = JSON.parse(assignation.settings || "{}")
                  const studentName = settings?.studentFullName || ""
                  const sendingByPosition = settings?.sendingByPosition || ""
                  const sendingByName = settings?.sendingByName || ""
                  const settingLanguage = settings?.language || ""
                  let settingCurrulum = settings?.curriculumName || ""
                  const curriculumId = settings?.curriculumId || 0
                  if (curriculumId > 0) {
                    const c = await getCurriculumReward({
                      groupId,
                      curriculumId,
                    })
                    if (c.status) {
                      settingCurrulum = c?.data?.name || settingCurrulum
                    }
                  }
                  const variantIndex =
                    json?.length - 1 < settings?.variantIndex
                      ? 0
                      : settings?.variantIndex
                  const achievementReason = (
                    json[variantIndex]?.achievementReason || ""
                  )
                    .replace(/{language}/g, settingLanguage)
                    .replace(/{curriculum}/g, settingCurrulum)
                  const achievementDescription = (
                    json[variantIndex]?.achievementDescription || ""
                  )
                    .replace(/{language}/g, settingLanguage)
                    .replace(/{curriculum}/g, settingCurrulum)
                  const encouragementMessage = (
                    json[variantIndex]?.encouragementMessage || ""
                  )
                    .replace(/{language}/g, settingLanguage)
                    .replace(/{curriculum}/g, settingCurrulum)
                  const title = item.name
                    ?.replace(/{language}/g, settingLanguage)
                    .replace(/{curriculum}/g, settingCurrulum)
                  data.push({
                    ...item,
                    readStatus: false,
                    userItemId: userRecognitionItemId,
                    categoryType: response?.recognitionSectionTypeId,
                    title,
                    name: `${studentName}`,
                    date,
                    teacher: sendingByName,
                    thumbnail: `${item.fullPath}/${item.thumbnailFileName}`,
                    image: `${item.fullPath}/${item.imageFileName}`,
                    reason: achievementReason,
                    dedication: achievementDescription,
                    message: encouragementMessage,
                    teacherTitle: sendingByPosition,
                    isKidDiploma: groupType < 7,
                    backgroundImage: `${window.location.origin}/recognitions/${settings?.backgroundImage}`,
                  })
                }
              } else {
                const assignation = response.assignations.find(
                  (a) => a.userId === userData?.userId
                )
                const date = moment(assignation?.visibleFrom).format(
                  "MMMM Do, YYYY"
                )
                const message = (
                  assignation?.notificationMessage || ""
                ).replace(/{date}/g, date)
                const assignmentFromMessage =
                  assignation?.assignmentFromMessage || ""
                data.push({
                  assignmentFromMessage,
                  readStatus: false,
                  userItemId: userRecognitionItemId,
                  image: `${response.fullPath}/${response.imageFileName}`,
                  audioFile: `${response.fullPath}/${response.audioFileName}`,
                  message: message || "",
                  categoryType: response?.recognitionSectionTypeId,
                })
              }
            }
          } catch (err) {
            console.log("🚀 ~ getUnreadRewards ~ err:", err)
          }
          // console.log("🚀 ~ getUnreadRewards ~ data:", data)
          setItems(data)
          if (data.length === 0) {
            console.warn("No unread rewards found")
            // call on close from parent
            if (
              runningFromIframe &&
              typeof window?.parent?.closeRewards === "function"
            ) {
              window.parent?.closeRewards()
            }
            if (params?.onClose) {
              params?.onClose()
            }
          }
        })
        .catch((err) => {
          console.log("🚀 ~ getUnreadRewards ~ err:", err)
          // call on close from parent
          if (
            runningFromIframe &&
            typeof window?.parent?.closeRewards === "function"
          ) {
            window.parent?.closeRewards()
          }
          if (params?.onClose) {
            params?.onClose()
          }
        })

      return
    }
    if (itemId > 0) {
      getDetailsByItem({ itemId })
    } else {
      if (userItemId > 0) {
        getDetailsByAsignationId({ userItemId })
      }
    }
  }, [queryParams, params, userData?.userId, selectedGroupData?.groupId])

  const getFromParams = async (results) => {
    const groupType =
      selectedGroupData?.groupTypeId > 0
        ? selectedGroupData?.groupTypeId
        : GROUP_TYPES.SECONDARY_REGULAR_CLASS
    const runningFromIframe = isRunningFromIframe()
    const data = []
    try {
      for (const response of results) {
        const userRecognitionItemId =
          response.assignations[0]?.userRecognitionItemId || 0
        if (response?.recognitionSectionTypeId === 3) {
          const groupId = selectedGroupData?.groupId
          const item = response
          const json = JSON.parse(item.description)
          const assignation = item.assignations.find(
            (a) => a.userId === userData?.userId
          )
          if (assignation) {
            const date = moment(assignation?.visibleFrom).format(
              "MMMM Do, YYYY"
            )
            const settings = JSON.parse(assignation.settings || "{}")
            const studentName = settings?.studentFullName || ""
            const sendingByPosition = settings?.sendingByPosition || ""
            const sendingByName = settings?.sendingByName || ""
            const settingLanguage = settings?.language || ""
            let settingCurrulum = settings?.curriculumName || ""
            const curriculumId = settings?.curriculumId || 0
            if (curriculumId > 0) {
              const c = await getCurriculumReward({
                groupId,
                curriculumId,
              })
              if (c.status) {
                settingCurrulum = c?.data?.name || settingCurrulum
              }
            }
            const variantIndex =
              json?.length - 1 < settings?.variantIndex
                ? 0
                : settings?.variantIndex
            const achievementReason = (
              json[variantIndex]?.achievementReason || ""
            )
              .replace(/{language}/g, settingLanguage)
              .replace(/{curriculum}/g, settingCurrulum)
            const achievementDescription = (
              json[variantIndex]?.achievementDescription || ""
            )
              .replace(/{language}/g, settingLanguage)
              .replace(/{curriculum}/g, settingCurrulum)
            const encouragementMessage = (
              json[variantIndex]?.encouragementMessage || ""
            )
              .replace(/{language}/g, settingLanguage)
              .replace(/{curriculum}/g, settingCurrulum)
            const title = item.name
              ?.replace(/{language}/g, settingLanguage)
              .replace(/{curriculum}/g, settingCurrulum)
            data.push({
              ...item,
              readStatus: false,
              userItemId: userRecognitionItemId,
              categoryType: response?.recognitionSectionTypeId,
              title,
              name: `${studentName}`,
              date,
              teacher: sendingByName,
              thumbnail: `${item.fullPath}/${item.thumbnailFileName}`,
              image: `${item.fullPath}/${item.imageFileName}`,
              reason: achievementReason,
              dedication: achievementDescription,
              message: encouragementMessage,
              teacherTitle: sendingByPosition,
              isKidDiploma: groupType < 7,
              backgroundImage: `${window.location.origin}/recognitions/${settings?.backgroundImage}`,
            })
          }
        } else {
          const assignation = response.assignations.find(
            (a) => a.userId === userData?.userId
          )
          const date = moment(assignation?.visibleFrom).format("MMMM Do, YYYY")
          const message = (assignation?.notificationMessage || "").replace(
            /{date}/g,
            date
          )
          const assignmentFromMessage = assignation?.assignmentFromMessage || ""
          data.push({
            assignmentFromMessage,
            readStatus: false,
            userItemId: userRecognitionItemId,
            image: `${response.fullPath}/${response.imageFileName}`,
            audioFile: `${response.fullPath}/${response.audioFileName}`,
            message: message || "",
            categoryType: response?.recognitionSectionTypeId,
          })
        }
      }
    } catch (err) {
      console.log("🚀 ~ getUnreadRewards ~ err:", err)
    }
    // console.log("🚀 ~ getUnreadRewards ~ data:", data)
    setItems(data)
    if (data.length === 0) {
      console.warn("No unread rewards found")
      // call on close from parent
      if (
        runningFromIframe &&
        typeof window?.parent?.closeRewards === "function"
      ) {
        window.parent?.closeRewards()
      }
      if (params?.onClose) {
        params?.onClose()
      }
    }
  }

  const getDetailsByItem = async ({ itemId }) => {
    const groupId = selectedGroupData?.groupId
    const response = await getReward({ groupId, itemId })
    getDetails({ response })
  }

  const getDetailsByAsignationId = async ({ userItemId }) => {
    const response = await getRewardByAssignations({ userItemId })
    getDetails({ response })
  }

  const getDetails = async ({ response }) => {
    const groupId = selectedGroupData?.groupId
    const groupType =
      selectedGroupData?.groupTypeId > 0
        ? selectedGroupData?.groupTypeId
        : GROUP_TYPES.SECONDARY_REGULAR_CLASS
    if (
      response?.assignations?.find((a) => a.userId === userData?.userId) !==
      undefined
    ) {
      const userRecognitionItemId =
        response.assignations[0]?.userRecognitionItemId || 0
      if (response?.recognitionSectionTypeId === 3) {
        const item = response
        const json = JSON.parse(item.description)
        const assignation = item.assignations.find(
          (a) => a.userId === userData?.userId
        )
        if (assignation) {
          const date = moment(assignation?.visibleFrom).format("MMMM Do, YYYY")
          const settings = JSON.parse(assignation.settings || "{}")
          const studentName = settings?.studentFullName || ""
          const sendingByPosition = settings?.sendingByPosition || ""
          const sendingByName = settings?.sendingByName || ""
          const settingLanguage = settings?.language || ""
          let settingCurrulum = settings?.curriculumName || ""
          const curriculumId = settings?.curriculumId || 0
          if (curriculumId > 0) {
            const c = await getCurriculumReward({ groupId, curriculumId })
            if (c.status) {
              settingCurrulum = c?.data?.name || settingCurrulum
            }
          }
          const variantIndex =
            json?.length - 1 < settings?.variantIndex
              ? 0
              : settings?.variantIndex
          const achievementReason = (
            json[variantIndex]?.achievementReason || ""
          )
            .replace(/{language}/g, settingLanguage)
            .replace(/{curriculum}/g, settingCurrulum)
          const achievementDescription = (
            json[variantIndex]?.achievementDescription || ""
          )
            .replace(/{language}/g, settingLanguage)
            .replace(/{curriculum}/g, settingCurrulum)
          const encouragementMessage = (
            json[variantIndex]?.encouragementMessage || ""
          )
            .replace(/{language}/g, settingLanguage)
            .replace(/{curriculum}/g, settingCurrulum)
          const title = item.name
            ?.replace(/{language}/g, settingLanguage)
            .replace(/{curriculum}/g, settingCurrulum)
          setItems([
            {
              ...item,
              readStatus: false,
              userItemId: userRecognitionItemId,
              categoryType: response?.recognitionSectionTypeId,
              title,
              name: `${studentName}`,
              date,
              teacher: sendingByName,
              thumbnail: `${item.fullPath}/${item.thumbnailFileName}`,
              image: `${item.fullPath}/${item.imageFileName}`,
              reason: achievementReason,
              dedication: achievementDescription,
              message: encouragementMessage,
              teacherTitle: sendingByPosition,
              isKidDiploma: groupType < 7,
              backgroundImage: `${window.location.origin}/recognitions/${settings?.backgroundImage}`,
            },
          ])
        }
      } else {
        const assignation = response.assignations.find(
          (a) => a.userId === userData?.userId
        )
        const date = moment(assignation?.visibleFrom).format("MMMM Do, YYYY")
        const message = (assignation?.notificationMessage || "").replace(
          /{date}/g,
          date
        )
        const assignmentFromMessage = assignation?.assignmentFromMessage || ""
        setItems([
          {
            assignmentFromMessage,
            userItemId: userRecognitionItemId,
            image: `${response.fullPath}/${response.imageFileName}`,
            audioFile: `${response.fullPath}/${response.audioFileName}`,
            message: message || "",
            categoryType: response?.recognitionSectionTypeId,
            readStatus: false,
          },
        ])
      }
    }
  }

  const onDownload = async (evt) => {
    evt?.preventDefault()
    if (loading) return
    setLoading(true)
    try {
      MySwal.fire({
        html: (
          <Stack className="laoder-modal">
            <LoaderModal />
          </Stack>
        ),
        showCancelButton: false,
        showConfirmButton: false,
      })
      await generatePdf()
    } catch (error) {
      console.log("~ onDownload ~ error:", error)
      setLoading(false)
      MySwal.close()
    }
    setLoading(false)
    MySwal.close()
  }

  const generatePdf = async () => {
    try {
      if (activeItemIndex < 0) return
      const input = diplomaContainersRef.current[activeItemIndex]
      const pdf = new JsPDF("l", "pt", "letter")
      const canvas = await html2canvas(input, {
        scale: 2,
        useCORS: true,
        backgroundColor: null,
      })
      const imgData = canvas.toDataURL("image/png")
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()

      const padding = 16

      const imgWidth = pageWidth - padding * 2
      const imgHeight = canvas.height * (imgWidth / canvas.width)

      // Centrar la imagen en el PDF
      const xOffset = padding // Posición X con padding
      const yOffset = (pageHeight - imgHeight) / 2 // Centrar verticalmente

      pdf.addImage(imgData, "PNG", xOffset, yOffset, imgWidth, imgHeight)
      pdf.save("diploma.pdf")
    } catch (error) {
      console.log("🚀 ~ generatePdf ~ error:", error)
    }
  }

  const onClose = (evt) => {
    // get classes of target
    const runningFromIframe = isRunningFromIframe()
    const classes = evt?.target?.classList || []
    if (
      !classes.contains("closeBtn") &&
      (evt.target.tagName === "BUTTON" ||
        evt.target.tagName === "svg" ||
        evt.target.tagName === "path")
    ) {
      return
    }
    // call on close from parent
    if (
      runningFromIframe &&
      typeof window?.parent?.closeRewards === "function"
    ) {
      window.parent?.closeRewards()
    }
    if (params?.onClose) {
      console.log("🚀 ~ onClose ~ params.onClose:", params.onClose)
      params?.onClose(evt)
    }
  }

  const readReward = (item) => {
    // console.log("readStatus", readStatusRef.current)
    const readStatus = readStatusRef.current?.includes(item.userItemId)
    // console.log("read", readStatus, item.userItemId)
    if (!readStatus) {
      setRewardRead({
        userItemId:
          process.env.REACT_APP_ENVIORNMENT === "development"
            ? 0
            : item?.userItemId || 0,
      })
      readStatusRef.current = [...readStatusRef.current, item.userItemId]
    }
  }

  const onPaginationUpdated = ({ index: activeIndex }) => {
    console.log("🚀 ~ onPaginationUpdated ~:", activeIndex)
    setActiveItemIndex(activeIndex) // Guardar el ítem activo en el estado
    const item = items[activeIndex]
    if (item?.userItemId) {
      readReward(item)
    }
  }

  const handleMounted = (splide) => {
    // Podrías intentar un refresh aquí si el problema persiste,
    // pero idealmente Splide debería calcularlo bien al montar si las imágenes tienen dimensiones.
    splide.refresh()
    console.log("splide mounted.")
    // set first item
    const item = items[0]
    if (item?.userItemId) {
      readReward(item)
    }
  }

  const renderItem = useCallback(({ item, index }) => {
    if (item.categoryType === 3) {
      return (
        <Box
          key={index}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="safe center"
        // p={1}
        // sx={{   height: "90vh",   }}
        >
          <Box
            ref={(el) => {
              diplomaContainersRef.current[index] = el
            }}
            sx={{
              top: -1000000,
              left: -1000000,
              position: "absolute",
            }}
          >
            <Diploma
              width="960px"
              height="700px"
              textSize={100}
              {...item}
              studentName={item?.name || ""}
              teacherName={item?.teacher || ""}
              smashyImage={item?.thumbnail}
              diplomasBg={item?.backgroundImage}
            />
          </Box>
          <Box
            sx={{
              overflow: "auto",
              maxHeight: `calc(94vh - ${items.length > 1 ? 82 : 52}px)`,
            }}
          >
            <Diploma
              width={"960px"}
              height="700px"
              textSize={100}
              // onImageLoaded={generateImage}
              {...item}
              studentName={item?.name || ""}
              teacherName={item?.teacher || ""}
              smashyImage={item?.thumbnail}
              diplomasBg={item?.backgroundImage}
            />
          </Box>
          <Box sx={styles.donwloadBtnContainer}>
            <Button
              sx={styles.donwloadBtn}
              variant="contained"
              disableElevation
              color="info"
              disabled={loading}
              onClick={onDownload}
            >
              {t("download")}
              <KeyboardArrowDownIcon />
            </Button>
          </Box>
        </Box>
      )
    }
    if (item?.categoryType === 1 || item?.categoryType === 2) {
      return (
        <Box
          position="relative"
          // mb={"20px"}
          height="calc(100vh - 50px)"
          key={index}
        >
          <StickerItem
            key={item.image}
            {...item}
            imageStyle={2} // 2
            containerStyle={styles.stickerContainer}
            textStyle={styles.stickerText}
            sx={{
              maxHeight: `calc(94vh - ${items.length > 1 ? 152 : 82}px)`,
            }}
            audioProps={{
              autoPlay: false,
              ref: (el) => {
                audiosRef.current[index] = el
              },
            }}
          />
        </Box>
      )
    }
  }, [])

  const onPrev = (evt) => {
    evt?.stopPropagation()
    // go to next page
    if (splideRef.current) {
      playNextNotification()
      if (v2) {
        splideRef?.current?.slickPrev()
      } else {
        splideRef.current.go("<")
      }
    }
  }

  const onNext = (evt) => {
    evt?.stopPropagation()
    // go to next page
    if (splideRef.current) {
      playNextNotification()
      if (v2) {
        splideRef?.current?.slickNext()
      } else {
        splideRef.current.go(">")
      }
    }
  }

  return (
    <Box className="dialog" onClick={onClose}>
      <Box className="backdrop" sx={styles.backdrop} onClick={onClose} />
      <Box
        className="dialogContainer"
        onClick={onClose}
        sx={styles.dialogContainer}
      >
        <Box
          className="paper"
          sx={[
            styles.paper,
            activeItemIndex >= 0 &&
            items[activeItemIndex]?.categoryType !== 3 &&
            styles.stickerBg,
          ]}
        >
          <CloseButton
            onClick={onClose}
            sx={{
              ...styles.closeBtn,
              right:
                items?.length > 1 || items[activeItemIndex]?.categoryType !== 3
                  ? -15
                  : -75,
            }}
          />
          <Box className="dialogContent" sx={styles.dialogContent}>
            {items?.length > 1 && (
              <Button sx={[styles.arrow, styles.arrowLeft]} onClick={onPrev}>
                <KeyboardArrowRightIcon />
              </Button>
            )}
            {v2 && (
              <div style={styles.content}>
                <Slider
                  ref={splideRef}
                  {...settings}
                  afterChange={(index) => onPaginationUpdated({ index })}
                >
                  {items.map((item, index) => {
                    return renderItem({ item, index })
                  })}
                </Slider>
              </div>
            )}
            {!v2 && items.length > 0 && (
              <Splide
                onPaginationUpdated={onPaginationUpdated}
                onMounted={handleMounted}
                ref={splideRef}
                id="rewards-carousel"
                hasTrack={false}
                options={{
                  type: "loop",
                  gap: "1rem",
                  rewind: true,
                  pagination: items?.length > 1,
                  arrows: false,
                  autoplay: false,
                  autoWidth: false,
                  width: "100vw",
                  height: "94vh",
                  drag: false,
                }}
                aria-label="rewards-carousel"
              >
                <div style={styles.content}>
                  <SplideTrack>
                    {items.map((item, index) => {
                      return (
                        <SplideSlide key={index}>
                          {item.categoryType === 3 && (
                            <Box
                              // ref={containerRef}
                              display="flex"
                              flexDirection="column"
                              alignItems="center"
                              justifyContent="safe center"
                              p={1}
                              sx={{
                                height: "94vh",
                                //   backgroundColor: "white",
                                // maxHeight: "calc(94vh - 92px)",
                                //  width: "100%",
                                //  maxWidth: 900,
                                //   overflow: "auto"
                              }}
                            >
                              <Box
                                ref={(el) => {
                                  diplomaContainersRef.current[index] = el
                                }}
                                sx={{
                                  top: -1000000,
                                  left: -1000000,
                                  position: "absolute",
                                }}
                              >
                                <Diploma
                                  width="960px"
                                  height="700px"
                                  textSize={100}
                                  // onImageLoaded={generateImage}
                                  {...item}
                                  studentName={item?.name || ""}
                                  teacherName={item?.teacher || ""}
                                  smashyImage={item?.thumbnail}
                                  diplomasBg={item?.backgroundImage}
                                />
                              </Box>
                              <Box
                                sx={{
                                  overflow: "auto",
                                  maxHeight: `calc(94vh - ${items.length > 1 ? 82 : 52
                                    }px)`,
                                  // maxWidth: 900,
                                  mb: 1,
                                  // borderRadius: "8px"
                                  //  border: "1px solid red",
                                }}
                              >
                                <Diploma
                                  width={"960px"}
                                  height="700px"
                                  textSize={100}
                                  // onImageLoaded={generateImage}
                                  {...item}
                                  studentName={item?.name || ""}
                                  teacherName={item?.teacher || ""}
                                  smashyImage={item?.thumbnail}
                                  diplomasBg={item?.backgroundImage}
                                />
                              </Box>
                              <Button
                                sx={styles.donwloadBtn}
                                variant="contained"
                                disableElevation
                                color="info"
                                disabled={loading}
                                onClick={onDownload}
                              >
                                {t("download")}
                                <KeyboardArrowDownIcon />
                              </Button>
                            </Box>
                          )}
                          {(item?.categoryType === 1 ||
                            item?.categoryType === 2) && (
                              <Box
                                position="relative"
                                mb={"20px"}
                                key={item.image}
                              >
                                <StickerItem
                                  key={item.image}
                                  {...item}
                                  imageStyle={2} // 2
                                  containerStyle={styles.stickerContainer}
                                  textStyle={styles.stickerText}
                                  sx={{
                                    maxHeight: `calc(94vh - ${items.length > 1 ? 152 : 82
                                      }px)`,
                                  }}
                                  audioProps={{
                                    autoPlay: false,
                                    ref: (el) => {
                                      audiosRef.current[index] = el
                                    },
                                  }}
                                />
                              </Box>
                            )}
                        </SplideSlide>
                      )
                    })}
                  </SplideTrack>
                </div>
              </Splide>
            )}
            {items?.length > 1 && (
              <Button sx={[styles.arrow, { right: "1rem" }]} onClick={onNext}>
                <KeyboardArrowRightIcon />
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
